import React, { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, ClipboardList, Search, Trash2 } from "lucide-react";
import api from "../../../services/api";
import {
  ConfirmDialog,
  Dialog,
  EmptyState,
  Loading,
  Pagination,
  cleanParams,
  errorText,
  formatDate,
  formatScore,
  useToast,
} from "./common";

const LETTERS = "ABCDEFGHIJ";

// correct_answer may be the option text, a letter ("B"), an "B) text" prefix, or an index.
const isCorrectOption = (option, index, correct) => {
  if (correct === null || correct === undefined) return false;
  if (typeof correct === "number") return correct === index;
  const c = String(correct).trim();
  const o = String(option).trim();
  if (!c) return false;
  if (c.toLowerCase() === o.toLowerCase()) return true;
  if (/^[A-Ja-j]$/.test(c)) return LETTERS.indexOf(c.toUpperCase()) === index;
  if (/^\d+$/.test(c) && !/^\d+$/.test(o)) return Number(c) === index;
  const prefix = c.match(/^([A-Ja-j])[).:\s]/);
  if (prefix) return LETTERS.indexOf(prefix[1].toUpperCase()) === index;
  return false;
};

const quizTitle = (q) => q?.topic || q?.subject || "Untitled quiz";

const QuizDrawer = ({ quizId, onClose, onDelete }) => {
  const [quiz, setQuiz] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!quizId) return;
    setQuiz(null);
    setError(null);
    try {
      const res = await api.get(`/admin/quizzes/${quizId}`);
      setQuiz(res.data);
    } catch (err) {
      setError(errorText(err, "Couldn't load this quiz."));
    }
  }, [quizId]);

  useEffect(() => {
    load();
  }, [load]);

  const questions = Array.isArray(quiz?.questions) ? quiz.questions : [];

  return (
    <Dialog
      open={Boolean(quizId)}
      onClose={onClose}
      variant="drawer"
      size="lg"
      title={quiz ? quizTitle(quiz) : "Quiz"}
      subtitle={
        quiz
          ? [quiz.subject, quiz.grade ? `Grade ${quiz.grade}` : null, `${questions.length} questions`]
              .filter(Boolean)
              .join(" · ")
          : undefined
      }
      footer={
        quiz && (
          <button type="button" className="eg-btn admin-btn-danger" onClick={() => onDelete(quiz)}>
            <Trash2 size={16} /> Delete quiz
          </button>
        )
      }
    >
      {error ? (
        <div className="admin-error admin-error-row">
          <span>{error}</span>
          <button type="button" className="eg-btn eg-btn-outline" onClick={load}>
            Try again
          </button>
        </div>
      ) : !quiz ? (
        <Loading label="Loading quiz…" />
      ) : (
        <>
          <p className="admin-field-hint">
            Created {formatDate(quiz.created_at)}
            {quiz.created_by ? ` by ${quiz.created_by}` : ""}. The correct answer is highlighted.
          </p>
          {questions.length === 0 ? (
            <EmptyState icon={ClipboardList} title="This quiz has no questions" />
          ) : (
            <ol className="admin-questions">
              {questions.map((q, qi) => {
                const options = Array.isArray(q.options) ? q.options : [];
                const anyMarked = options.some((o, i) => isCorrectOption(o, i, q.correct_answer));
                return (
                  <li key={qi} className="admin-question">
                    <p className="admin-question-text">
                      <span>{qi + 1}.</span> {q.question}
                    </p>
                    <ul className="admin-options">
                      {options.map((opt, oi) => {
                        const correct = isCorrectOption(opt, oi, q.correct_answer);
                        return (
                          <li key={oi} className={correct ? "correct" : ""}>
                            <span className="admin-option-letter">{LETTERS[oi] || oi + 1}</span>
                            <span className="admin-option-text">{opt}</span>
                            {correct && (
                              <span className="admin-option-mark">
                                <CheckCircle2 size={16} aria-hidden="true" />
                                <span className="sr-only">Correct answer</span>
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    {!anyMarked && q.correct_answer !== undefined && q.correct_answer !== null && (
                      <p className="admin-field-hint">
                        Correct answer: <strong>{String(q.correct_answer)}</strong>
                      </p>
                    )}
                    {q.explanation && (
                      <p className="admin-explanation">
                        <strong>Why:</strong> {q.explanation}
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </>
      )}
    </Dialog>
  );
};

const QuizzesPanel = () => {
  const { notify } = useToast();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const req = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/admin/quizzes", {
        params: cleanParams({ search: search.trim(), page, limit: 15 }),
      });
      if (req !== requestRef.current) return;
      setData({
        items: Array.isArray(res.data?.items) ? res.data.items : [],
        total: res.data?.total ?? 0,
        pages: res.data?.pages || 1,
      });
      setLoaded(true);
    } catch (err) {
      if (req === requestRef.current) setError(errorText(err, "Couldn't load quizzes."));
    } finally {
      if (req === requestRef.current) setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const askDelete = (quiz) => {
    setDeleteError(null);
    setToDelete(quiz);
  };

  const doDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/admin/quizzes/${toDelete.quiz_id}`);
      notify(`Quiz "${quizTitle(toDelete)}" deleted.`);
      if (openId === toDelete.quiz_id) setOpenId(null);
      setToDelete(null);
      if (data.items.length === 1 && page > 1) setPage((p) => p - 1);
      else load();
    } catch (err) {
      setDeleteError(errorText(err, "Couldn't delete this quiz."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="dashboard-section admin-panel">
      <div className="admin-toolbar">
        <div className="admin-search">
          <Search size={18} aria-hidden="true" />
          <input
            type="search"
            placeholder="Search by subject, topic or grade"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            aria-label="Search quizzes"
          />
        </div>
      </div>

      {error && (
        <div className="admin-error admin-error-row">
          <span>{error}</span>
          <button type="button" className="eg-btn eg-btn-outline" onClick={load}>
            Try again
          </button>
        </div>
      )}

      <div className={`admin-table-wrap ${loading && loaded ? "is-refreshing" : ""}`} aria-busy={loading}>
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col">Quiz</th>
              <th scope="col">Grade</th>
              <th scope="col">Questions</th>
              <th scope="col">Attempts</th>
              <th scope="col">Avg score</th>
              <th scope="col">Created</th>
              <th scope="col"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {!loaded && loading ? (
              <tr>
                <td colSpan={7} className="admin-muted">Loading quizzes…</td>
              </tr>
            ) : data.items.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyState icon={ClipboardList} title={search.trim() ? "No quizzes match your search" : "No quizzes yet"}>
                    {!search.trim() && "Quizzes generated by students and teachers will appear here."}
                  </EmptyState>
                </td>
              </tr>
            ) : (
              data.items.map((q) => (
                <tr key={q.quiz_id} className="admin-row-click" onClick={() => setOpenId(q.quiz_id)}>
                  <td>
                    <div className="admin-cell-stack">
                      <strong>{quizTitle(q)}</strong>
                      <small>
                        {[q.topic ? q.subject : null, q.created_by ? `by ${q.created_by}` : null]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </small>
                    </div>
                  </td>
                  <td>{q.grade || "—"}</td>
                  <td>{q.questions_count ?? "—"}</td>
                  <td>{q.attempts ?? 0}</td>
                  <td>{q.attempts ? formatScore(q.average_score) : "—"}</td>
                  <td className="admin-nowrap">{formatDate(q.created_at)}</td>
                  <td>
                    <div className="admin-row-actions">
                      <button
                        type="button"
                        className="admin-link-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenId(q.quiz_id);
                        }}
                      >
                        View
                      </button>
                      <button
                        type="button"
                        className="admin-icon-btn admin-icon-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          askDelete(q);
                        }}
                        aria-label={`Delete quiz ${quizTitle(q)}`}
                        title="Delete quiz"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pages={data.pages} total={data.total} noun="quiz" plural="quizzes" onPage={setPage} disabled={loading} />

      <QuizDrawer quizId={openId} onClose={() => setOpenId(null)} onDelete={askDelete} />

      <ConfirmDialog
        open={Boolean(toDelete)}
        title="Delete this quiz?"
        confirmLabel="Delete quiz"
        busy={deleting}
        error={deleteError}
        onConfirm={doDelete}
        onCancel={() => setToDelete(null)}
      >
        <p>
          <strong>{quizTitle(toDelete)}</strong> and its questions will be removed. Students won't be able to
          open it again. This can't be undone.
        </p>
      </ConfirmDialog>
    </div>
  );
};

export default QuizzesPanel;
